/* Original BlackPatel UI for the free Dawn theme. Native Dawn owns cart and variant requests. */
(() => {
  'use strict';
  if (window.BPFreeCommerce) return;

  // Merchant size data: centimetres. No body measurements are inferred for collar/cuff sets.
  const measurements = Object.freeze({
    'XS/S': ['73–84', '55–66', '80–90'],
    'S/M': ['84–97', '66–76', '90–100'],
    'L/XL': ['97–107', '76–86', '100–111'],
    '2XL/3XL': ['107–115', '86–99', '111–121'],
    '4XL/5XL': ['115–129', '99–110', '121–131'],
    '6XL/7XL': ['129–141', '110–121', '131–141'],
  });
  const state = new Map();
  let scheduled = false;
  let toastTimer;
  let toastFallback;
  let pendingToast;

  const range = (value, unit) => unit === 'cm' ? value : value.split('–').map(n => (Number(n) / 2.54).toFixed(1)).join('–');
  const keyFor = picker => picker.dataset.productId || picker.dataset.productHandle || picker.id;
  const stateFor = picker => {
    const key = keyFor(picker);
    if (!state.has(key)) state.set(key, { unit: 'in', expanded: false });
    return state.get(key);
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function sizeOptions(picker) {
    const options = [...picker.querySelectorAll('input[data-option-name], option[data-option-name]')]
      .filter(input => /^(size|beden)$/i.test(input.dataset.optionName));
    return { options, selected: options.find(input => input.checked || input.selected)?.value,
      field: options[0]?.closest('fieldset, .product-form__input') };
  }

  function renderSizeCard(picker) {
    const { options, selected, field } = sizeOptions(picker);
    if (!field) return;
    field.classList.add('bp-free-option--size');
    const sizes = [...new Set(options.map(input => input.value))].filter(size => measurements[size]);
    const collar = picker.dataset.productHandle === 'cleo-o-halkali-siyah-boyun-ve-bileklik-seti';
    if (collar || !sizes.length || !measurements[selected]) {
      picker.querySelector('.bp-free-size-card')?.remove();
      return;
    }
    const current = stateFor(picker);
    let card = picker.querySelector('.bp-free-size-card');
    const signature = JSON.stringify({ sizes, selected, unit: current.unit });
    if (card?.dataset.signature === signature) return;
    // Keep the first server-rendered card intact: hydrate its listener without replacing layout.
    if (card?.hasAttribute('data-bp-free-ssr') && !card.dataset.signature &&
        card.dataset.selectedSize === selected && card.dataset.unit === current.unit &&
        card.dataset.sizes === sizes.join('|')) {
      card.dataset.signature = signature;
      const details = card.querySelector('details');
      details.open = current.expanded;
      details.addEventListener('toggle', () => { current.expanded = details.open; });
      return;
    }
    if (card?.dataset.signature) current.expanded = card.querySelector('details').open;
    const next = element('section', 'bp-free-size-card');
    next.dataset.signature = signature;
    next.setAttribute('aria-label', 'Size measurements');
    const header = element('div', 'bp-free-size-card__header');
    const title = element('p', 'bp-free-size-card__title', `${selected} measurements`);
    title.setAttribute('aria-live', 'polite');
    const units = element('div', 'bp-free-size-card__units');
    units.setAttribute('role', 'group');
    units.setAttribute('aria-label', 'Measurement unit');
    [['in', 'in'], ['cm', 'cm']].forEach(([value, label]) => {
      const button = element('button', '', label);
      button.type = 'button';
      button.dataset.bpFreeUnit = value;
      button.setAttribute('aria-pressed', String(current.unit === value));
      units.append(button);
    });
    header.append(title, units);
    const summary = element('dl', 'bp-free-size-card__measurements');
    ['Bust', 'Waist', 'Hips'].forEach((label, index) => {
      const cell = element('div');
      cell.append(element('dt', '', label), element('dd', '', range(measurements[selected][index], current.unit)));
      summary.append(cell);
    });
    const details = element('details', 'bp-free-size-card__details');
    details.open = current.expanded;
    details.append(element('summary', '', 'View size guide'));
    const scroll = element('div', 'bp-free-size-card__table-wrap');
    const table = element('table');
    const caption = element('caption', 'visually-hidden', `Body measurements in ${current.unit === 'cm' ? 'centimetres' : 'inches'}`);
    const head = element('thead');
    const tr = element('tr');
    ['Size', 'Bust', 'Waist', 'Hips'].forEach(label => {
      const th = element('th', '', label); th.scope = 'col'; tr.append(th);
    });
    head.append(tr);
    const body = element('tbody');
    sizes.forEach(size => {
      const row = element('tr');
      if (size === selected) row.className = 'is-selected';
      const label = element('th', '', size); label.scope = 'row'; row.append(label);
      measurements[size].forEach(value => row.append(element('td', '', range(value, current.unit))));
      body.append(row);
    });
    table.append(caption, head, body); scroll.append(table);
    const note = element('p', 'bp-free-size-card__note', `Measurements in ${current.unit === 'cm' ? 'cm' : 'inches, rounded to one decimal'}. If you are between sizes, choose the larger size. Fit varies by design.`);
    details.append(scroll, note);
    details.addEventListener('toggle', () => { current.expanded = details.open; });
    next.append(header, summary, details);
    if (card) card.replaceWith(next);
    else (picker.querySelector('.bp-free-option--color') || [...picker.querySelectorAll('.product-form__input')].at(-1) || field).after(next);
  }

  function enhanceColor(picker) {
    picker.querySelectorAll('input[data-option-name]').forEach(input => {
      if (!/^(color|colour|renk)$/i.test(input.dataset.optionName)) return;
      const field = input.closest('fieldset');
      const label = input.nextElementSibling;
      if (!field || label?.tagName !== 'LABEL') return;
      field.classList.add('bp-free-option--color');
      if (/^(black|siyah)$/i.test(input.value)) label.classList.add('bp-free-swatch--black');
      const legend = field.querySelector('legend');
      if (legend && input.checked) {
        let value = legend.querySelector('.bp-free-option-value');
        if (!value) { value = element('span', 'bp-free-option-value'); legend.append(value); }
        value.textContent = `: ${input.value}`;
      }
    });
  }

  function updateDialog(picker) {
    const id = picker.querySelector('[data-bp-size-open]')?.dataset.bpSizeOpen;
    const dialog = id && document.getElementById(id);
    if (!dialog) return;
    const { unit } = stateFor(picker);
    const { selected } = sizeOptions(picker);
    dialog.querySelectorAll('[data-cm]').forEach(cell => { cell.textContent = range(cell.dataset.cm, unit); });
    dialog.querySelectorAll('.bp-unit-switch input').forEach(input => { input.checked = input.value === unit; });
    dialog.querySelectorAll('tbody tr').forEach(row => row.classList.toggle('bp-free-size-selected', row.querySelector('th')?.textContent.trim() === selected));
    const note = dialog.querySelector('.bp-size-note');
    if (note) note.textContent = `Measurements are in ${unit === 'cm' ? 'centimetres' : 'inches, rounded to one decimal'}. If you are between sizes, choose the larger size. Fit varies by design.`;
  }

  function refresh() {
    scheduled = false;
    document.querySelectorAll('variant-selects').forEach(picker => {
      renderSizeCard(picker); enhanceColor(picker); updateDialog(picker);
    });
  }
  function scheduleRefresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(refresh);
  }

  function hideToast() {
    clearTimeout(toastTimer);
    const toast = document.getElementById('BPFreeCartToast');
    if (!toast) return;
    // Do not leave keyboard focus inside a hidden confirmation.
    if (toast.contains(document.activeElement)) document.getElementById('cart-icon-bubble')?.focus();
    toast.hidden = true;
  }
  function scheduleDismiss() {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      const toast = document.getElementById('BPFreeCartToast');
      if (toast?.contains(document.activeElement) || toast?.matches(':hover')) return;
      hideToast();
    }, 6500);
  }
  function showToast(data) {
    const toast = document.getElementById('BPFreeCartToast');
    if (!toast) return;
    const title = data.cartData?.product_title || data.cartData?.title || '';
    toast.querySelector('[data-bp-free-cart-item]').textContent = title;
    toast.hidden = false;
    scheduleDismiss();
  }
  function flushToast() {
    clearTimeout(toastFallback);
    if (!pendingToast) return;
    const data = pendingToast; pendingToast = null;
    showToast(data);
  }
  function onCartUpdate(data) {
    if (data?.source !== 'product-form' || !data.cartData || data.cartData.status) return;
    pendingToast = data;
    if (document.querySelector('quick-add-modal[open]')) {
      clearTimeout(toastFallback);
      toastFallback = setTimeout(flushToast, 600);
    } else flushToast();
  }

  function init() {
    refresh();
    document.addEventListener('change', event => {
      const picker = event.target.closest('variant-selects');
      if (picker) { renderSizeCard(picker); enhanceColor(picker); updateDialog(picker); }
      if (event.target.matches('.bp-unit-switch input')) {
        const dialog = event.target.closest('.bp-size-dialog');
        document.querySelectorAll('variant-selects').forEach(owner => {
          if (owner.querySelector('[data-bp-size-open]')?.dataset.bpSizeOpen === dialog?.id) {
            stateFor(owner).unit = event.target.value; renderSizeCard(owner); updateDialog(owner);
          }
        });
      }
    });
    document.addEventListener('click', event => {
      const unit = event.target.closest('[data-bp-free-unit]');
      if (unit) {
        const picker = unit.closest('variant-selects');
        if (picker) {
          const value = unit.dataset.bpFreeUnit;
          stateFor(picker).unit = value; renderSizeCard(picker); updateDialog(picker);
          picker.querySelector(`[data-bp-free-unit="${value}"]`)?.focus({ preventScroll: true });
        }
      }
      if (event.target.closest('[data-bp-free-toast-close]')) hideToast();
      const view = event.target.closest('[data-bp-free-cart-open]');
      const drawer = document.querySelector('cart-drawer');
      if (view && drawer?.open) {
        event.preventDefault();
        hideToast();
        drawer.open(document.getElementById('cart-icon-bubble') || view);
      }
    });
    document.body.addEventListener('modalClosed', () => { if (pendingToast) setTimeout(flushToast, 0); });
    const toast = document.getElementById('BPFreeCartToast');
    toast?.addEventListener('mouseenter', () => clearTimeout(toastTimer));
    toast?.addEventListener('mouseleave', scheduleDismiss);
    toast?.addEventListener('focusin', () => clearTimeout(toastTimer));
    toast?.addEventListener('focusout', scheduleDismiss);

    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
        !node.closest('.bp-free-size-card') &&
        (node.matches('variant-selects, product-info, fieldset') || node.querySelector('variant-selects'))))) scheduleRefresh();
    }).observe(document.body, { childList: true, subtree: true });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && toast) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      subscribe(PUB_SUB_EVENTS.variantChange, scheduleRefresh);
      // Root's Dawn adapter only suppresses automatic drawer opening once this listener exists.
      document.documentElement.setAttribute('data-bp-free-commerce', '');
    }
    document.addEventListener('shopify:section:load', scheduleRefresh);
  }
  window.BPFreeCommerce = Object.freeze({ refresh: scheduleRefresh });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
